import type { ReactNode } from 'react';
import type { Tier } from '@shared/hooks/useBreakpoint';
import { CustomerAvatar } from './CustomerAvatar';
import type { CustomerRow, CustomerStatus, RegisterSort, StatusTab } from './types';

// 11a — the customer register.
//
// The artboards' geometry over the fields the customer record already carries:
// CUSTOMER · ORGANISATION · STATUS · TAGS · LAST SEEN · CREATED. No column here
// needs a query that does not exist yet, so the screen works against the
// deployed backend as it stands.
//
// What survives from the artboard, because it is the argument rather than the
// decoration: a 28px round avatar with three variants, a row whose only
// emphasis is a 2px left border for a blocked record, status as a bordered chip
// rather than a filled pill, and tabs that carry live counts.

// Column widths, §2.3 geometry mapped onto the columns this build has.
const W = {
  check: 38,
  organisation: 196,
  status: 104,
  tags: 150,
  lastSeen: 126,
  created: 118,
  menu: 38,
} as const;

/**
 * Which optional columns survive at this tier.
 *
 * Drop order: TAGS first, then CREATED, then ORGANISATION collapses under the
 * name. STATUS and LAST SEEN never drop — they are what the register is read
 * for.
 */
function columnsFor(tier: Tier) {
  return {
    tags: tier === 'full',
    created: tier === 'full' || tier === 'laptop',
    organisation: tier !== 'tabletP',
  };
}

const MONO = 'font-mono';

/** Status is a real status, so it is one of the three hues; anything else is ink. */
const STATUS_TONE: Record<CustomerStatus, { color: string; border: string; wash?: string }> = {
  active: { color: 'var(--co-green-ink)', border: 'var(--co-green-line)', wash: 'var(--co-green-wash)' },
  inactive: { color: 'var(--co-ink-3)', border: 'var(--co-line-strong)' },
  blocked: {
    color: 'var(--co-burgundy)',
    border: 'var(--co-burgundy-line)',
    wash: 'var(--co-burgundy-wash)',
  },
  pending: {
    color: 'var(--co-terracotta)',
    border: 'var(--co-terracotta-line)',
    wash: 'var(--co-terracotta-wash)',
  },
  merged: { color: 'var(--co-machine-ink)', border: 'var(--co-machine-line)' },
};

function relativeDay(iso: string | null): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'never';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Tabs ────────────────────────────────────────────────────────────────────
// One per status, counted live from the status-count endpoint. This is the old
// screen's row of status cards, which was already a filter — the same flow, in
// the row the artboard puts it in.
const TABS: { id: StatusTab; label: string; tone: string }[] = [
  { id: 'all', label: 'ALL', tone: 'var(--co-ink-3-soft)' },
  { id: 'active', label: 'ACTIVE', tone: 'var(--co-green-ink)' },
  { id: 'inactive', label: 'INACTIVE', tone: 'var(--co-ink-4)' },
  { id: 'blocked', label: 'BLOCKED', tone: 'var(--co-burgundy)' },
];

export function StatusTabs({
  active,
  counts,
  total,
  onSelect,
  sortLabel,
  onOpenSort,
}: {
  active: StatusTab;
  /** Keyed by status, from GET /customers/stats/status-counts. */
  counts: Record<string, number> | null;
  /** The register's own total, which is what ALL means. */
  total: number | null;
  onSelect: (tab: StatusTab) => void;
  sortLabel: string;
  onOpenSort: () => void;
}) {
  // 26b draws the cohort control as a SEGMENTED GROUP on a sunken tray, not as
  // underlined tabs. The behaviour is untouched — each segment still writes the
  // same `status` URL param the underlined tab did.
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-[6px] rounded-lg bg-[var(--co-sunken)] p-1">
        {TABS.map((t) => {
          const on = t.id === active;
          const count = t.id === 'all' ? (total ?? undefined) : counts?.[t.id];
          return (
            <button
              key={t.id}
              type="button"
              aria-current={on ? 'page' : undefined}
              onClick={() => onSelect(t.id)}
              className={`${MONO} flex items-center gap-[6px] rounded-md px-3 py-[7px] text-[10.5px] tracking-[.12em]`}
              style={{
                background: on ? 'var(--co-raised)' : 'transparent',
                boxShadow: on ? 'var(--co-shadow)' : undefined,
                color: on ? 'var(--co-ink)' : 'var(--co-ink-2)',
                fontWeight: on ? 600 : 500,
              }}
            >
              {t.label}
              {/* No count rather than a confident 0 while the counts are in
                  flight — a 0 that later becomes 61 is worse than a gap. */}
              {count !== undefined && (
                <span style={{ color: on ? t.tone : 'var(--co-ink-3)', fontWeight: 600 }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onOpenSort}
        className="flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] px-3 hover:border-[var(--co-brass)] hover:bg-[var(--co-selected)]"
      >
        <span
          className={`${MONO} text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}
        >
          Sort
        </span>
        <span className="text-[12.5px] text-[var(--co-ink-2)]">{sortLabel}</span>
        <span className="text-[10px] text-[var(--co-ink-3)]" aria-hidden="true">
          ▾
        </span>
      </button>
    </div>
  );
}

function StatusChip({ status }: { status: CustomerStatus }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.inactive;
  return (
    <span
      className={`${MONO} inline-flex items-center rounded-full border px-[9px] py-[3px] text-[9.5px] font-semibold tracking-[.1em]`}
      style={{ borderColor: tone.border, background: tone.wash, color: tone.color }}
    >
      {status.toUpperCase()}
    </span>
  );
}

function ColumnHead({
  children,
  width,
  align = 'left',
  className = '',
  sortDirection,
  onSort,
}: {
  children?: ReactNode;
  width?: number;
  align?: 'left' | 'right';
  className?: string;
  /** 'asc' | 'desc' when THIS column is the active sort, else undefined/null. */
  sortDirection?: 'asc' | 'desc' | null;
  /** Present only on columns the backend can sort by (Bug 9164) — plain text otherwise. */
  onSort?: () => void;
}) {
  return (
    <div
      role="columnheader"
      aria-sort={onSort ? (sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none') : undefined}
      className={`${MONO} ${width ? 'flex-none' : 'min-w-0 flex-1'} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)] ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
      style={width ? { width } : undefined}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className={`inline-flex items-center gap-[3px] hover:text-[var(--co-ink-2)] ${
            align === 'right' ? 'flex-row-reverse' : ''
          }`}
        >
          {children}
        </button>
      ) : (
        children
      )}
    </div>
  );
}

// ── The row ─────────────────────────────────────────────────────────────────
export function RegisterRow({
  row,
  tier,
  selected,
  onToggle,
  onOpen,
  onMenu,
}: {
  row: CustomerRow;
  tier: Tier;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const cols = columnsFor(tier);
  const blocked = row.status === 'blocked';
  const displayName = row.name ?? row.email.split('@')[0];

  return (
    <div
      role="row"
      tabIndex={0}
      aria-selected={selected}
      // The whole row opens the customer. Clicks that land on the row's own
      // controls (checkbox, name, ⋯ menu) are theirs, not the row's, and a
      // drag-to-select-text must not navigate away.
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, input, a, label')) return;
        if (window.getSelection()?.toString()) return;
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter') onOpen();
        else if (e.key === ' ') {
          e.preventDefault();
          onToggle(!selected);
        }
      }}
      className={`flex cursor-pointer items-center gap-3 border-b border-[var(--co-line-subtle)] px-5 py-[11px] hover:bg-[var(--co-selected)] focus-visible:bg-[var(--co-selected)] focus-visible:outline-none ${
        selected ? 'bg-[var(--co-selected)]' : ''
      }`}
      style={{
        // The only row-level emphasis on the screen. No background tint — a
        // tinted row competes with the chip that carries the actual status.
        borderLeft: `2px solid ${blocked ? 'var(--co-burgundy)' : 'transparent'}`,
      }}
    >
      {/* A label, so the whole cell is the hit area and a near-miss toggles the
          checkbox instead of opening the customer. */}
      <label
        className="flex flex-none cursor-pointer items-center justify-center self-stretch"
        style={{ width: W.check }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Select ${displayName}`}
          className="h-[14px] w-[14px] cursor-pointer rounded-[3px] border border-[var(--co-line)] accent-[var(--co-brass)]"
        />
      </label>

      {/* CUSTOMER */}
      <div className="flex min-w-0 flex-1 items-center gap-[11px]">
        <CustomerAvatar
          initials={row.avatarInitials}
          variant={blocked ? 'blocked' : row.status === 'inactive' ? 'outlined' : 'filled'}
        />
        <div className="min-w-0">
          <button
            type="button"
            onClick={onOpen}
            className="block min-w-0 max-w-full truncate text-left text-[13.5px] hover:underline"
            style={{
              fontWeight: blocked ? 500 : 600,
              color: blocked ? 'var(--co-ink-3)' : 'var(--co-ink)',
              textDecoration: blocked ? 'line-through' : undefined,
              textDecorationColor: blocked ? 'var(--co-line-strong)' : undefined,
            }}
          >
            {displayName}
          </button>
          {/* Email and phone on one mono line, as the artboard has them. */}
          <div className={`${MONO} mt-[3px] truncate text-[11px] text-[var(--co-ink-4)]`}>
            {[row.email, row.phone].filter(Boolean).join(' · ')}
          </div>
          {/* Ticket count — every ticket on record for this customer, any
              status. Muted at zero, brass when there's history to look at. */}
          <div
            className={`${MONO} mt-[3px] truncate text-[10px] tracking-[.08em]`}
            style={{
              color: row.ticketCount > 0 ? 'var(--co-brass-ink)' : 'var(--co-ink-4)',
            }}
          >
            {row.ticketCount} TICKET{row.ticketCount === 1 ? '' : 'S'}
          </div>
          {/* At the narrowest tier the organisation collapses under the name
              rather than disappearing. */}
          {!cols.organisation && row.organisation && (
            <div className={`${MONO} mt-[3px] truncate text-[10px] tracking-[.08em] text-[var(--co-ink-4)]`}>
              {row.organisation.name.toUpperCase()}
            </div>
          )}
        </div>
      </div>

      {/* ORGANISATION */}
      {cols.organisation && (
        <div className="min-w-0 flex-none" style={{ width: W.organisation }}>
          {row.organisation ? (
            <>
              <div className="truncate text-[12.5px] text-[var(--co-ink-2)]">
                {row.organisation.name}
              </div>
              {/* Plan and seats sit under the name because they are what decide
                  how hard to push. Absent when the org record does not carry
                  them — never a guessed default. */}
              {(row.organisation.plan || row.organisation.seats !== null) && (
                <div className={`${MONO} mt-[3px] truncate text-[10px] tracking-[.08em] text-[var(--co-ink-4)]`}>
                  {[
                    row.organisation.plan?.toUpperCase(),
                    row.organisation.seats !== null ? `${row.organisation.seats} SEATS` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </>
          ) : (
            <span className={`${MONO} text-[11px] text-[var(--co-ink-3)]`}>—</span>
          )}
        </div>
      )}

      {/* STATUS */}
      <div className="flex-none" style={{ width: W.status }}>
        <StatusChip status={row.status} />
      </div>

      {/* TAGS */}
      {cols.tags && (
        <div className="flex flex-none flex-wrap items-center gap-1" style={{ width: W.tags }}>
          {row.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className={`${MONO} rounded-full border px-[8px] py-[3px] text-[9.5px] font-medium tracking-[.08em]`}
              style={{
                // VIP had the brass border in the artboard; this palette has no
                // accent, so it earns weight and ink instead of a hue.
                borderColor: 'var(--co-line-strong)',
                color: tag.toUpperCase() === 'VIP' ? 'var(--co-ink)' : 'var(--co-ink-3)',
                fontWeight: tag.toUpperCase() === 'VIP' ? 600 : 500,
              }}
            >
              {tag.toUpperCase()}
            </span>
          ))}
          {row.tags.length > 2 && (
            <span className={`${MONO} text-[9.5px] text-[var(--co-ink-4)]`}>
              {`+${row.tags.length - 2}`}
            </span>
          )}
        </div>
      )}

      {/* LAST SEEN */}
      <div
        className={`${MONO} flex-none text-right text-[12px] font-medium tabular-nums`}
        style={{
          width: W.lastSeen,
          color: row.lastSeenAt ? 'var(--co-ink)' : 'var(--co-ink-3)',
        }}
      >
        {relativeDay(row.lastSeenAt)}
      </div>

      {/* CREATED */}
      {cols.created && (
        <div
          className={`${MONO} flex-none text-right text-[12px] tabular-nums text-[var(--co-ink-2)]`}
          style={{ width: W.created }}
        >
          {shortDate(row.createdAt)}
        </div>
      )}

      <div className="flex flex-none justify-center" style={{ width: W.menu }}>
        <button
          type="button"
          onClick={onMenu}
          aria-label={`Actions for ${displayName}`}
          className={`${MONO} text-[13px] text-[var(--co-ink-3)] hover:text-[var(--co-ink)]`}
        >
          ⋯
        </button>
      </div>
    </div>
  );
}

export function RegisterHead({
  tier,
  allSelected,
  onSelectAllOnPage,
  sortBy,
  sortOrder,
  onSort,
}: {
  tier: Tier;
  allSelected: boolean;
  onSelectAllOnPage: (checked: boolean) => void;
  sortBy: RegisterSort;
  sortOrder: 'asc' | 'desc';
  /**
   * Click-to-sort on the column headers (Bug 9164). Sorting already worked
   * via the Filters panel's "Sort By" control — this wires the SAME
   * sortBy/sortOrder mechanism onto the headers themselves, matching the
   * Tickets list, rather than adding a second sorting path. Only the
   * three columns the backend can order by (RegisterSort) are wired; STATUS,
   * ORGANISATION and TAGS stay plain text because there is no sortBy value
   * for them.
   */
  onSort?: (field: RegisterSort) => void;
}) {
  const cols = columnsFor(tier);
  const arrow = sortOrder === 'asc' ? ' ↑' : ' ↓';
  return (
    <div
      role="row"
      className="flex items-center gap-3 border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px]"
      style={{ borderLeft: '2px solid transparent' }}
    >
      <div className="flex flex-none items-center justify-center" style={{ width: W.check }}>
        {/* This checkbox means THIS PAGE and nothing else. The bar it opens is
            where "all n" is offered, explicitly and with the true count. */}
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onSelectAllOnPage(e.target.checked)}
          aria-label="Select every customer on this page"
          title="Selects this page only"
          className="h-[14px] w-[14px] rounded-[3px] border border-[var(--co-line)] accent-[var(--co-brass)]"
        />
      </div>
      <ColumnHead
        sortDirection={(sortBy === 'name' || sortBy === 'email') ? sortOrder : null}
        onSort={onSort ? () => onSort('name') : undefined}
      >
        {`CUSTOMER${sortBy === 'name' || sortBy === 'email' ? arrow : ''}`}
      </ColumnHead>
      {cols.organisation && <ColumnHead width={W.organisation}>ORGANISATION</ColumnHead>}
      <ColumnHead width={W.status}>STATUS</ColumnHead>
      {cols.tags && <ColumnHead width={W.tags}>TAGS</ColumnHead>}
      <ColumnHead
        width={W.lastSeen}
        align="right"
        sortDirection={sortBy === 'lastSeenAt' ? sortOrder : null}
        onSort={onSort ? () => onSort('lastSeenAt') : undefined}
      >
        {`LAST SEEN${sortBy === 'lastSeenAt' ? arrow : ''}`}
      </ColumnHead>
      {cols.created && (
        <ColumnHead
          width={W.created}
          align="right"
          sortDirection={sortBy === 'createdAt' ? sortOrder : null}
          onSort={onSort ? () => onSort('createdAt') : undefined}
        >
          {`CREATED${sortBy === 'createdAt' ? arrow : ''}`}
        </ColumnHead>
      )}
      <ColumnHead width={W.menu} />
    </div>
  );
}
